import { Loader2, FolderKanban, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StatusEditor } from './StatusEditor';
import { DeleteStatusReassignDialog } from './DeleteStatusReassignDialog';
import {
  useListStatuses,
  useEnableCustomStatuses,
  useDisableCustomStatuses,
  useCreateListStatus,
  useUpdateListStatus,
  useDeleteListStatus,
  useReorderListStatuses,
} from '@/hooks/use-lists';
import { toast } from '@/components/ui/toaster';
import { isStatusDeleteTasksConflict } from '@/lib/api';
import { getContrastTextColor } from '@/lib/utils';
import { useState } from 'react';
import type { List } from '@/types';

type ApiErrorLike = { response?: { data?: { message?: string } } };

interface ListStatusesDialogProps {
  list: List;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ListStatusesDialog({
  list,
  projectName,
  open,
  onOpenChange,
}: ListStatusesDialogProps) {
  const { data: statuses = [], isLoading } = useListStatuses(list.id);
  const enableCustom = useEnableCustomStatuses();
  const disableCustom = useDisableCustomStatuses();
  const createStatus = useCreateListStatus();
  const updateStatus = useUpdateListStatus();
  const deleteStatus = useDeleteListStatus();
  const reorderStatuses = useReorderListStatuses();

  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [reassign, setReassign] = useState<{ statusId: string; taskCount: number } | null>(null);

  const handleEnableCustom = async () => {
    try {
      await enableCustom.mutateAsync({
        listId: list.id,
        projectId: list.projectId,
      });
      toast({ title: 'Custom statuses enabled' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to enable custom statuses',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleDisableCustom = async () => {
    try {
      await disableCustom.mutateAsync({
        listId: list.id,
        projectId: list.projectId,
      });
      toast({ title: 'Reverted to project statuses' });
      setShowDisableConfirm(false);
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to disable custom statuses',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = async (data: { name: string; color: string; isDoneState: boolean }) => {
    try {
      await createStatus.mutateAsync({
        listId: list.id,
        projectId: list.projectId,
        ...data,
      });
      toast({ title: 'Status created' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to create status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleUpdate = async (
    statusId: string,
    data: { name?: string; color?: string; isDoneState?: boolean }
  ) => {
    try {
      await updateStatus.mutateAsync({
        listId: list.id,
        statusId,
        ...data,
      });
      toast({ title: 'Status updated' });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to update status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDelete = async (statusId: string) => {
    try {
      await deleteStatus.mutateAsync({
        listId: list.id,
        projectId: list.projectId,
        statusId,
      });
      toast({ title: 'Status deleted' });
    } catch (error: unknown) {
      if (isStatusDeleteTasksConflict(error)) {
        const taskCount = error.response?.data?.taskCount ?? 0;
        setReassign({ statusId, taskCount });
        return;
      }
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to delete status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleReassignConfirm = async (replacementStatusId: string) => {
    if (!reassign) return;
    try {
      await deleteStatus.mutateAsync({
        listId: list.id,
        projectId: list.projectId,
        statusId: reassign.statusId,
        replacementStatusId,
      });
      toast({
        title: 'Status deleted',
        description: 'Existing tasks were moved to the selected status.',
      });
      setReassign(null);
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to delete status',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const handleReorder = async (statusIds: string[]) => {
    try {
      await reorderStatuses.mutateAsync({ listId: list.id, statusIds });
    } catch (error: unknown) {
      const { response } = error as ApiErrorLike;
      toast({
        title: 'Failed to reorder statuses',
        description: response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
      throw error;
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>List Statuses</DialogTitle>
            <DialogDescription>
              Manage statuses for <strong>{list.name}</strong> in {projectName}.
            </DialogDescription>
          </DialogHeader>

          {/* Status mode indicator */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border">
            {list.usesCustomStatuses ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Custom statuses</p>
                  <p className="text-xs text-muted-foreground">
                    This list uses its own custom statuses
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDisableConfirm(true)}
                  disabled={disableCustom.isPending}
                >
                  {disableCustom.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Use Project Statuses
                </Button>
              </>
            ) : (
              <>
                <FolderKanban className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Project statuses</p>
                  <p className="text-xs text-muted-foreground">
                    Using default statuses from {projectName}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEnableCustom}
                  disabled={enableCustom.isPending}
                >
                  {enableCustom.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Customize
                </Button>
              </>
            )}
          </div>

          <Separator />

          {/* Status editor */}
          {list.usesCustomStatuses ? (
            <StatusEditor
              statuses={statuses}
              isLoading={isLoading}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onReorder={handleReorder}
              isCreating={createStatus.isPending}
              isUpdating={updateStatus.isPending}
            />
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Current project statuses (read-only):
              </p>
              <div className="flex flex-wrap gap-2">
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  statuses.map((status) => {
                    const sBg = status.color || '#6b7280';
                    const sText = getContrastTextColor(sBg);
                    return (
                      <Badge
                        key={status.id}
                        className="font-medium"
                        style={{ backgroundColor: sBg, color: sText, borderColor: 'transparent' }}
                      >
                        {status.name}
                        {status.isDoneState && ' ✓'}
                      </Badge>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Click "Customize" to create custom statuses for this list.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm disable custom statuses */}
      <DeleteStatusReassignDialog
        open={!!reassign}
        onOpenChange={(next) => !next && setReassign(null)}
        excludeStatusId={reassign?.statusId ?? ''}
        taskCount={reassign?.taskCount ?? 0}
        statuses={statuses}
        isPending={deleteStatus.isPending}
        onConfirm={handleReassignConfirm}
      />

      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert to project statuses?</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p>
                  This will switch <strong>{list.name}</strong> back to using the default project statuses.
                </p>
                <div className="flex items-start gap-2 p-2 rounded bg-yellow-500/10 text-yellow-600 dark:text-yellow-400">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p className="text-sm">
                    Tasks will be mapped to project statuses by name. If any custom status doesn't have a matching project status, this action will be blocked.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableCustom}
              disabled={disableCustom.isPending}
            >
              {disableCustom.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Revert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
